import mixpanel from 'mixpanel-browser'
import { useEffect, useState } from 'react'

import type { CurrentUserResponse } from '@/api/types/user'
import { getUser } from '@/api/user'
import { ROLE_SUPER_ADMIN } from '@/constants/roles'
import { TOKEN_KEY } from '@/storage/constants'
import { getDataFromStorage } from '@/storage/storageRepository'

import type { GetMixpanel, MockedOverridedMixpanel } from './types'
import type { OverridedMixpanel } from 'mixpanel-browser'

let wasMixpanelInit = false

const warnMessageBase =
  'Mixpanel not initialized (missing REACT_APP_MIXPANEL_TOKEN)'

const mockedMixpanel: MockedOverridedMixpanel = {
  track: (eventName, options) => {
    // eslint-disable-next-line no-console
    console.group('Mixpanel not initialized (missing REACT_APP_MIXPANEL_TOKEN)')
    console.warn(`Event: "${eventName}" not sent`)
    console.warn(`Options: ${JSON.stringify(options, null, 2)}`)
    // eslint-disable-next-line no-console
    console.groupEnd()
  },
  identify: (id) => console.warn(`${warnMessageBase}; identify ${id} not sent`),
}

const getMixpanel: GetMixpanel = (user?: CurrentUserResponse) => {
  if (!wasMixpanelInit && process.env.MIXPANEL_TOKEN) {
    const isSuperAdmin = user?.systemRoleList.includes(ROLE_SUPER_ADMIN)

    mixpanel.init(process.env.MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV === 'development',
      secure_cookie: true,
      record_sessions_percent: isSuperAdmin ? undefined : 50,
      // maximum number of events/updates to send in a single
      // network request
      batch_size: 2,

      // milliseconds to wait between sending batch requests
      batch_flush_interval_ms: 200,
    })
    mixpanel.identify(user?.id.toString() || 'NO_USER_ID')
    wasMixpanelInit = true
  }
  if (wasMixpanelInit) {
    return mixpanel
  }
  return mockedMixpanel
}

const useMixpanel = (allowNoUser?: boolean) => {
  const [user, setUser] = useState<CurrentUserResponse | null>(null)
  const [mixpanel, setMixpanel] = useState<
    OverridedMixpanel | MockedOverridedMixpanel | null
  >(null)
  const token = getDataFromStorage(TOKEN_KEY)

  useEffect(() => {
    if (!user && token) {
      getUser().then((user) => {
        if (user) {
          setUser(user)
          setMixpanel(getMixpanel(user))
        }
      })
    } else if (allowNoUser) {
      setMixpanel(getMixpanel(undefined))
    }
  }, [user, token])
  return mixpanel
}

export { useMixpanel }
